import { motion } from "framer-motion";
import { FaWhatsapp, FaTelegram } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { Link2 } from "lucide-react";
import { useState } from "react";

export default function TruthCard({ truth }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `Truth #${truth.num}: ${truth.title} - 27 Brutal Truths About Trading`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <motion.div
      data-testid={`truth-card-${truth.num}`}
      className="w-full max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: 0.1 }}
    >
      {/* Truth number badge */}
      <div className="flex items-center gap-3 mb-4">
        <span
          className="inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold text-white font-mono"
          style={{ backgroundColor: "#FF007F", boxShadow: "0 0 15px rgba(255,0,127,0.4)" }}
        >
          {String(truth.num).padStart(2, "0")}
        </span>
        <h3 className="text-base sm:text-lg font-bold text-white uppercase tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          {truth.title}
        </h3>
      </div>

      {/* Slide image */}
      <div className="rounded-2xl overflow-hidden border border-[#27272A] bg-black relative group">
        <img
          src={truth.image}
          alt={`Truth #${truth.num}: ${truth.title}`}
          className="w-full h-auto object-contain"
          loading="lazy"
        />
        {/* Hover overlay with problem, truth, action */}
        <div className="absolute inset-0 bg-black/85 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-center items-center px-6 text-center pointer-events-none">
          <p className="text-[#FF007F] font-mono text-[10px] tracking-widest uppercase mb-2">Problem</p>
          <p className="text-white/70 text-sm mb-4 max-w-md">{truth.problem}</p>
          <p className="text-[#FF007F] font-mono text-[10px] tracking-widest uppercase mb-2">Truth</p>
          <p className="text-white font-semibold text-sm mb-4 max-w-md">{truth.truth}</p>
          <p className="text-[#FF007F] font-mono text-[10px] tracking-widest uppercase mb-2">Action</p>
          <p className="text-emerald-400 font-semibold text-sm max-w-md">{truth.action}</p>
        </div>
      </div>

      {/* Per-card share row */}
      <div className="flex items-center justify-center gap-3 mt-3">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-8 h-8 rounded-full flex items-center justify-center border border-[#27272A] bg-[#121212] hover:border-[#FF007F] transition-all duration-200 hover:scale-110"
          data-testid={`share-wa-${truth.num}`}
          title="Share on WhatsApp"
        >
          <FaWhatsapp size={14} color="#25D366" />
        </a>
        <a
          href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-8 h-8 rounded-full flex items-center justify-center border border-[#27272A] bg-[#121212] hover:border-[#FF007F] transition-all duration-200 hover:scale-110"
          data-testid={`share-tg-${truth.num}`}
          title="Share on Telegram"
        >
          <FaTelegram size={14} color="#0088CC" />
        </a>
        <a
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-8 h-8 rounded-full flex items-center justify-center border border-[#27272A] bg-[#121212] hover:border-[#FF007F] transition-all duration-200 hover:scale-110"
          data-testid={`share-x-${truth.num}`}
          title="Share on X"
        >
          <FaXTwitter size={14} color="#fff" />
        </a>
        <button
          onClick={copyLink}
          className="w-8 h-8 rounded-full flex items-center justify-center border border-[#27272A] bg-[#121212] hover:border-[#FF007F] transition-all duration-200 hover:scale-110"
          data-testid={`copy-link-${truth.num}`}
          title="Copy link"
        >
          <Link2 size={14} className={copied ? "text-emerald-400" : "text-[#71717A]"} />
        </button>
        {copied && <span className="text-emerald-400 text-xs font-mono">Copied!</span>}
      </div>
    </motion.div>
  );
}
